import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertCircle, Loader2, MessageSquare, Send, X } from 'lucide-react'
import Button from '@/components/Button'
import ErrorBanner from '@/components/ErrorBanner'
import Modal from '@/components/Modal'
import { useLanguage } from '@/contexts/LanguageContext'
import { logError, responseErrorMessage, toUserMessage } from '@/utils/errorMessage'
import { turbineService, type Comment } from '@/services/turbineService'
import { authService } from '@/services/authService'

// Comment Sidebar Component
const USER_LOAD_WARNING = 'Kullanıcı bilgisi alınamadı; yorum düzenleme ve silme seçenekleri görünmeyebilir.'

const CommentSidebar: React.FC<{
  turbineId: number
  isOpen: boolean
  onClose: () => void
}> = ({ turbineId, isOpen, onClose }) => {
  const { t } = useLanguage()

  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [editingComment, setEditingComment] = useState<number | null>(null)
  const [editText, setEditText] = useState('')
  const [expandedComments, setExpandedComments] = useState<Set<number>>(new Set())

  // Liste yuklenemedi (Kural 4): mevcut yorumlar KORUNUR, "yuklenemedi" ayri gosterilir.
  const [loadError, setLoadError] = useState<string | null>(null)
  // Kullanici bilgisi alinamadiginda duzenle/sil butonlari sebepsiz kaybolur;
  // bu durumu sessiz birakmak yerine acikliyoruz.
  const [userWarning, setUserWarning] = useState<string | null>(null)
  // Yorum gonderme / duzenleme hatasi. Sidebar bir DRAWER oldugu icin hata sayfa
  // govdesine degil, kullanicinin baktigi bu yuzeye yazilir (Kural 1).
  const [actionError, setActionError] = useState<string | null>(null)

  // Silme onayi: `window.confirm` yerine hata gosterebilen bir modal (Kural 7).
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)
  const [deletingComment, setDeletingComment] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  // Load comments and user data
  useEffect(() => {
    if (isOpen) {
      loadComments()
      loadUserData()
    }
  }, [isOpen, turbineId])

  const loadUserData = async () => {
    try {
      const userData = await authService.getCurrentUser()
      setUser(userData)
      // DİKKAT: authService.getCurrentUser() SENKRONdur ve throw ETMEZ -- bozuk ya da
      // eksik oturum verisinde `null` döner. Yani asıl sessiz durum aşağıdaki catch
      // değil, bu `null` dönüşüdür: kullanıcı bilgisi yoksa düzenle/sil butonları
      // hiçbir açıklama olmadan kaybolur.
      setUserWarning(userData ? null : USER_LOAD_WARNING)
    } catch (error) {
      logError('CommentSidebar.loadUserData', error)
      setUserWarning(USER_LOAD_WARNING)
    }
  }

  const loadComments = async () => {
    try {
      setLoading(true)
      const response = await turbineService.getComments(turbineId)
      if (response.success) {
        setComments(response.data ?? [])
        setLoadError(null)
      } else {
        // Kural 5: success:false bir hatadir. Kural 4: liste [] yapilmaz.
        setLoadError(responseErrorMessage(response, t, t.errors.loadFailed))
      }
    } catch (error) {
      logError('CommentSidebar.loadComments', error)
      setLoadError(toUserMessage(error, t, t.errors.loadFailed))
    } finally {
      setLoading(false)
    }
  }

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return
    if (submitting) return // Kural 10: cift gonderim engeli

    setActionError(null)

    try {
      setSubmitting(true)
      const response = await turbineService.addComment(turbineId, newComment.trim())
      if (response.success && response.data) {
        // Add the new comment to the list immediately (optimistic update)
        setComments(prev => [response.data!, ...prev])
        setNewComment('')
      } else if (response.success) {
        // Kaydedildi ama govde donmedi: metni temizleyip listeyi tazeliyoruz.
        setNewComment('')
        await loadComments()
      } else {
        // Kural 5: HTTP 200 + success:false sessizce yutulmaz.
        setActionError(responseErrorMessage(response, t, 'Yorum eklenirken hata oluştu'))
      }
    } catch (error) {
      // Onceden yalnizca console.error vardi: yorum gitmiyordu ve kullanici bunu
      // hicbir yerde goremiyordu. Artik hata drawer'in icinde yazili kalir.
      logError('CommentSidebar.handleSubmitComment', error)
      setActionError(toUserMessage(error, t, 'Yorum eklenirken hata oluştu'))
      // Reload on error to ensure consistency
      await loadComments()
    } finally {
      setSubmitting(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  // Check if user can edit/delete comment (1 hour limit or superadmin)
  const canEditComment = (comment: Comment) => {
    if (!user) return false
    
    // Superadmin can edit all comments
    if (user.role === 'superadmin') return true
    
    // User can edit their own comments within 1 hour
    if (comment.user_id === user.id) {
      const commentTime = new Date(comment.created_at).getTime()
      const now = new Date().getTime()
      const oneHour = 60 * 60 * 1000 // 1 hour in milliseconds
      return (now - commentTime) < oneHour
    }
    
    return false
  }

  // Handle comment edit
  const handleEditComment = async (commentId: number) => {
    if (!editText.trim()) return

    setActionError(null)

    try {
      const response = await turbineService.updateComment(turbineId, commentId, editText.trim())
      if (response.success) {
        // Update the comment in the list immediately (optimistic update)
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, comment: editText.trim(), updated_at: new Date().toISOString() }
            : comment
        ))
        setEditingComment(null)
        setEditText('')
      } else {
        // Kural 5: success:false bir hatadir; duzenleme formu ACIK kalir.
        setActionError(responseErrorMessage(response, t, 'Yorum güncellenirken hata oluştu'))
      }
    } catch (error) {
      // `alert(...)` yerine drawer ici bant: sayfayi bloke etmez, metni okunur kilar.
      logError('CommentSidebar.handleEditComment', error)
      setActionError(toUserMessage(error, t, 'Yorum güncellenirken hata oluştu'))
      // Reload on error to ensure consistency
      await loadComments()
    }
  }

  // Handle comment delete
  const requestDeleteComment = (commentId: number) => {
    setDeleteError(null)
    setPendingDeleteId(commentId)
  }

  const closeDeleteDialog = () => {
    if (deletingComment) return
    setPendingDeleteId(null)
    setDeleteError(null)
  }

  const confirmDeleteComment = async () => {
    if (pendingDeleteId === null) return
    if (deletingComment) return // Kural 10: cift gonderim engeli

    const commentId = pendingDeleteId
    setDeletingComment(true)
    setDeleteError(null)

    try {
      const response = await turbineService.deleteComment(turbineId, commentId)
      if (response.success) {
        // Remove the comment from the list immediately (optimistic update)
        setComments(prev => prev.filter(comment => comment.id !== commentId))
        setPendingDeleteId(null)
      } else {
        // Kural 7: yikici islem hata verdiyse onay modali KAPANMAZ.
        setDeleteError(responseErrorMessage(response, t, 'Yorum silinirken hata oluştu'))
      }
    } catch (error) {
      logError('CommentSidebar.confirmDeleteComment', error)
      setDeleteError(toUserMessage(error, t, 'Yorum silinirken hata oluştu'))
    } finally {
      setDeletingComment(false)
    }
  }

  // Toggle comment expansion
  const toggleCommentExpansion = (commentId: number) => {
    const newExpanded = new Set(expandedComments)
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId)
    } else {
      newExpanded.add(commentId)
    }
    setExpandedComments(newExpanded)
  }

  // Truncate comment text
  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[9999]"
            onClick={onClose}
          />
          
          {/* Sidebar */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-96 bg-white shadow-xl z-[99999] flex flex-col"
          >
            {/* Header */}
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-blue-500" />
                Yorumlar
              </h3>
              <Button
                onClick={onClose}
                variant="outline"
                size="sm"
                className="p-2"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Elde onceki yorumlar varsa veri KORUNUR, ustune uyari bandi cikar. */}
              {loadError && comments.length > 0 && (
                <ErrorBanner message={loadError} tone="warning" onRetry={loadComments} />
              )}

              {userWarning && (
                <ErrorBanner message={userWarning} tone="warning" onDismiss={() => setUserWarning(null)} />
              )}

              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                </div>
              ) : loadError && comments.length === 0 ? (
                /* Kural 4: "yuklenemedi" ile "henuz yorum yok" gorsel olarak AYRISIR. */
                <div className="text-center py-8">
                  <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">{t.errors.loadFailed}</p>
                  <p className="text-xs text-gray-500 mt-1">{loadError}</p>
                  <button
                    type="button"
                    onClick={loadComments}
                    className="mt-3 px-3 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-medium transition-colors"
                  >
                    {t.errors.retry}
                  </button>
                </div>
              ) : comments.length > 0 ? (
                comments.map((comment) => {
                  const isExpanded = expandedComments.has(comment.id)
                  const isLongComment = comment.comment.length > 100
                  const canEdit = canEditComment(comment)
                  const isEditing = editingComment === comment.id
                  
                  return (
                    <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-sm text-gray-900">
                              {comment.user_name}
                            </span>
                            <span className="text-xs text-gray-500">
                              {formatDate(comment.created_at)}
                            </span>
                            {comment.updated_at && comment.updated_at !== comment.created_at && (
                              <span className="text-xs text-blue-500 italic">
                                (düzenlendi)
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Edit/Delete buttons */}
                        {canEdit && !isEditing && (
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => {
                                setActionError(null)
                                setEditingComment(comment.id)
                                setEditText(comment.comment)
                              }}
                              className="text-gray-400 hover:text-blue-500 transition-colors p-1"
                              title="Yorumu düzenle"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => requestDeleteComment(comment.id)}
                              className="text-gray-400 hover:text-red-500 transition-colors p-1"
                              title="Yorumu sil"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Comment content */}
                      {isEditing ? (
                        <div className="space-y-2">
                          <textarea
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={3}
                            maxLength={500}
                          />
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-400">
                              {editText.length}/500
                            </span>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleEditComment(comment.id)}
                                disabled={!editText.trim()}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs disabled:opacity-50"
                              >
                                Kaydet
                              </button>
                              <button
                                onClick={() => {
                                  setEditingComment(null)
                                  setEditText('')
                                  setActionError(null)
                                }}
                                className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded text-xs"
                              >
                                İptal
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                            {isLongComment && !isExpanded 
                              ? truncateText(comment.comment)
                              : comment.comment
                            }
                          </p>
                          {isLongComment && (
                            <button
                              onClick={() => toggleCommentExpansion(comment.id)}
                              className="text-xs text-blue-500 hover:text-blue-600 mt-1 font-medium"
                            >
                              {isExpanded ? 'Daha az göster' : 'Daha fazla göster'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )
                })
              ) : (
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Henüz yorum yok</p>
                  <p className="text-gray-300 text-xs mt-1">İlk yorumu siz yapın!</p>
                </div>
              )}
            </div>

            {/* Comment Input */}
            <div className="p-4 border-t">
              <div className="space-y-3">
                {/* Gonderme / duzenleme hatasi: drawer'in icinde, aksiyonun yaninda. */}
                <ErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Yorumunuzu yazın..."
                  className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  maxLength={500}
                />
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">
                    {newComment.length}/500
                  </span>
                  <Button
                    onClick={handleSubmitComment}
                    disabled={!newComment.trim() || submitting}
                    className="bg-blue-500 hover:bg-blue-600 text-white disabled:opacity-50"
                    size="sm"
                  >
                    {submitting ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Gönder
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Yorum silme onayi. `window.confirm` hata gosteremiyordu; bu modal
              basarisizlikta KAPANMAZ ve nedeni icinde gosterir. */}
          <Modal
            isOpen={pendingDeleteId !== null}
            onClose={closeDeleteDialog}
            title="Yorumu Sil"
            error={deleteError}
            onDismissError={() => setDeleteError(null)}
            isSubmitting={deletingComment}
            size="sm"
            footer={
              <div className="flex space-x-3 justify-end">
                <button
                  type="button"
                  onClick={closeDeleteDialog}
                  disabled={deletingComment}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  İptal
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteComment}
                  disabled={deletingComment}
                  className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {/* TODO(i18n): "Siliniyor..." icin strings.ts'te anahtar yok. */}
                  {deletingComment ? 'Siliniyor...' : t.common.delete}
                </button>
              </div>
            }
          >
            <p className="text-gray-600">
              Bu yorum silinecek. Bu işlem geri alınamaz. Emin misiniz?
            </p>
          </Modal>
        </>
      )}
    </AnimatePresence>
  )
}

export default CommentSidebar
